
import React, { useState } from 'react';
import { PlusCircle, Trash2, UserCheck } from 'lucide-react';

interface SettingsViewProps {
  whitelistedEmails: string[];
  onAddEmail: (email: string) => void;
  onRemoveEmail: (email: string) => void;
}

const SettingsView: React.FC<SettingsViewProps> = ({ whitelistedEmails, onAddEmail, onRemoveEmail }) => {
  const [newEmail, setNewEmail] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newEmail.trim() && /\S+@\S+\.\S+/.test(newEmail)) { // Basic email validation
      onAddEmail(newEmail.trim());
      setNewEmail('');
    } else {
      alert("Please enter a valid email address.");
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="text-3xl font-bold text-white mb-2">Settings: Access Control</h2>
        <p className="text-gray-400">Manage users who are permitted to use this application.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="glass-card rounded-xl p-6">
          <h3 className="text-xl font-bold text-white mb-4">Whitelist a New User</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email-input" className="text-sm font-semibold text-gray-400 mb-2 block">
                User's Email Address
              </label>
              <input
                id="email-input"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="e.g., jane.doe@example.com"
                className="w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white placeholder-gray-500 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-teal-600 to-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-xl transition-all flex items-center justify-center gap-2"
            >
              <PlusCircle size={20} />
              Add to Whitelist
            </button>
          </form>
        </div>

        <div className="glass-card rounded-xl p-6">
          <h3 className="text-xl font-bold text-white mb-4">Whitelisted Users ({whitelistedEmails.length})</h3>
          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
            {whitelistedEmails.length > 0 ? (
              whitelistedEmails.map((email) => (
                <div key={email} className="flex items-center justify-between p-4 bg-gray-800 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-3">
                    <UserCheck className="text-emerald-400" size={20} />
                    <span className="font-mono text-gray-200">{email}</span>
                  </div>
                  <button
                    onClick={() => onRemoveEmail(email)}
                    className="p-2 hover:bg-red-900/50 text-red-500 rounded-lg transition-colors"
                    aria-label={`Remove ${email} from whitelist`}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">No users have been whitelisted yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsView;
